import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const I18N_CONFIG_KEY = "i18nConfig";
const SUPPORTED = ["es", "en"];

function detectBrowserLang() {
	try {
		const nav = String(
			(typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage)) || ""
		)
			.trim()
			.toLowerCase();
		if (nav.startsWith("en")) return "en";
	} catch {
		/* ignore */
	}
	return "es";
}

export function getConfig() {
	const ls = localStorage.getItem(I18N_CONFIG_KEY);
	if (ls) {
		try {
			const parsed = JSON.parse(ls);
			const lang = String(parsed?.selectedLang || "").toLowerCase();
			if (SUPPORTED.includes(lang)) return { selectedLang: lang };
		} catch (er) {
			console.error(er);
		}
	}
	return { selectedLang: detectBrowserLang() };
}

function persistLanguage(lang) {
	const next = lang === "en" ? "en" : "es";
	localStorage.setItem(I18N_CONFIG_KEY, JSON.stringify({ selectedLang: next }));
	try {
		document.documentElement.lang = next;
	} catch {
		/* ignore */
	}
	return next;
}

/** @type {((lang: string) => void) | null} */
let languageSetter = null;

/** Cambia el idioma sin recargar (si el provider está montado). */
export function setLanguage(lang) {
	const next = persistLanguage(lang);
	if (typeof languageSetter === "function") {
		languageSetter(next);
		return;
	}
	// Fallback extremo (sin provider): recarga
	window.location.reload();
}

const I18nContext = createContext({ selectedLang: "es", setLanguage: setLanguage });

export function useLang() {
	return useContext(I18nContext)?.selectedLang || "es";
}

export function useSetLanguage() {
	return useContext(I18nContext)?.setLanguage || setLanguage;
}

export function withI18n(Component) {
	class WithI18n extends React.Component {
		static displayName = `WithI18n(${Component.displayName || Component.name})`;

		static contextType = I18nContext;

		render() {
			return <Component {...this.props} menu={this.context} />;
		}
	}

	return WithI18n;
}

export const I18nConsumer = I18nContext.Consumer;

export function CustomI18nProvider({ children }) {
	const [selectedLang, setSelectedLang] = useState(() => getConfig().selectedLang);

	const changeLanguage = useCallback((lang) => {
		const next = persistLanguage(lang);
		setSelectedLang(next);
	}, []);

	useEffect(() => {
		languageSetter = changeLanguage;
		return () => {
			if (languageSetter === changeLanguage) languageSetter = null;
		};
	}, [changeLanguage]);

	useEffect(() => {
		try {
			document.documentElement.lang = selectedLang === "en" ? "en" : "es";
		} catch {
			/* ignore */
		}
	}, [selectedLang]);

	const value = useMemo(
		() => ({ selectedLang, setLanguage: changeLanguage }),
		[selectedLang, changeLanguage]
	);

	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
